/**
 * WalletConnectSection Component
 * 
 * Multi-wallet management for Profile page.
 * Shows list of linked wallets with remove/set-primary controls.
 * Connect + link flow for adding new wallets.
 * 
 * Includes defensive guard against "ghost wallet" — wagmi state persisting
 * from a previous user's session.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAccount, useConnect, useDisconnect, useSignMessage, useSwitchChain } from 'wagmi';
import { base } from 'wagmi/chains';
import { useWallet } from '../contexts/WalletContext';
import '../styles/WalletConnect.css';

export default function WalletConnectSection({ customer, onUpdate }) {
  const { address, isConnected, chain } = useAccount();
  const { connect, connectors, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { switchChain } = useSwitchChain();
  
  const { wallets, walletsMax, walletsLoading, connectedIsLinked, atLimit, refetchWallets } = useWallet();

  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [updatingPref, setUpdatingPref] = useState(false);
  const [prefError, setPrefError] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [settingPrimaryId, setSettingPrimaryId] = useState(null);
  
  // Track if user clicked "Connect Wallet" in this session
  const [userInitiatedConnection, setUserInitiatedConnection] = useState(false);
  const userInitiatedRef = useRef(false);
  const [linkSuccess, setLinkSuccess] = useState(false);
  
  const currentPreference = customer?.preferred_payment_method || 'both';
  const linkedWallet = customer?.wallet_address;
  
  const isGhostWallet = isConnected && !userInitiatedConnection && !connectedIsLinked;

  const truncateAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Auto-switch to Base if on wrong chain
  useEffect(() => {
    if (isConnected && !isGhostWallet && chain && chain.id !== base.id) {
      switchChain?.({ chainId: base.id });
    }
  }, [isConnected, isGhostWallet, chain, switchChain]);

  const handleConnect = async () => {
    setLinkError('');
    setLinkSuccess(false);
    setUserInitiatedConnection(true);
    userInitiatedRef.current = true;

    // If already connected, force MetaMask account picker
    if (isConnected && window.ethereum) {
      try {
        disconnect();
        await window.ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }]
        });
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts[0]) {
          // Reconnect wagmi with the newly selected account
          const connector = connectors[0];
          if (connector) connect({ connector });
        }
      } catch (err) {
        userInitiatedRef.current = false;
        setUserInitiatedConnection(false);
        if (!err.message?.includes('User rejected')) {
          setLinkError(err.message || 'Failed to switch wallet');
        }
      }
      return;
    }

    const connector = connectors[0];
    if (connector) {
      connect({ connector });
    }
  };

  const handleLink = async () => {
    if (!address) return;
    
    setLinking(true);
    setLinkError('');
    
    try {
      const challengeRes = await fetch('/api/customer/wallet/challenge', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: address }),
      });
      
      if (!challengeRes.ok) {
        const err = await challengeRes.json();
        throw new Error(err.detail || 'Failed to get challenge');
      }
      
      const { nonce, message } = await challengeRes.json();
      const signature = await signMessageAsync({ message });
      
      const linkRes = await fetch('/api/customer/profile', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: address, signature, nonce }),
      });
      
      if (!linkRes.ok) {
        const err = await linkRes.json();
        if (linkRes.status === 409) {
          throw new Error('This wallet is already linked to another account');
        }
        throw new Error(err.detail || 'Failed to link wallet');
      }
      
      await refetchWallets();
      onUpdate?.();
      setUserInitiatedConnection(false);
      setLinkSuccess(true);
      setTimeout(() => setLinkSuccess(false), 3000);
      
    } catch (err) {
      console.error('Wallet link error:', err);
      if (err.message?.includes('User rejected') || err.message?.includes('denied')) {
        setLinkError('Signature cancelled — click Connect Another Wallet to try again');
      } else {
        setLinkError(err.message || 'Failed to link wallet');
      }
      disconnect();
      setUserInitiatedConnection(false);
    } finally {
      setLinking(false);
    }
  };

  // Auto-link: trigger signature immediately after user-initiated connect
  useEffect(() => {
    if (address && userInitiatedRef.current && !connectedIsLinked && !atLimit) {
      userInitiatedRef.current = false;
      handleLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const handleRemove = async (walletId) => {
    setRemovingId(walletId);
    try {
      const res = await fetch(`/api/customer/wallets/${walletId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Failed to remove wallet');
        return;
      }
      // Cascade: if the removed wallet matches the current wagmi connection, disconnect wagmi
      const removedWallet = wallets.find(w => w.id === walletId);
      if (removedWallet && address && removedWallet.wallet_address.toLowerCase() === address.toLowerCase()) {
        disconnect();
      }
      await refetchWallets();
      onUpdate?.();
    } catch (err) {
      console.error('Remove wallet error:', err);
    } finally {
      setRemovingId(null);
    }
  };

  const handleSetPrimary = async (walletId) => {
    setSettingPrimaryId(walletId);
    try {
      const res = await fetch(`/api/customer/wallets/${walletId}/primary`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Failed to set primary');
        return;
      }
      await refetchWallets();
      onUpdate?.();
    } catch (err) {
      console.error('Set primary error:', err);
    } finally {
      setSettingPrimaryId(null);
    }
  };

  const handlePreferenceChange = async (newPref) => {
    if (newPref === currentPreference) return;
    
    setUpdatingPref(true);
    setPrefError('');
    
    try {
      const res = await fetch('/api/customer/profile', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferred_payment_method: newPref }),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update preference');
      }
      
      onUpdate?.();
    } catch (err) {
      setPrefError(err.message || 'Failed to update preference');
    } finally {
      setUpdatingPref(false);
    }
  };

  return (
    <div className="wallet-connect-section">
      <h4>Wallet</h4>

      {/* Wallet List */}
      {!walletsLoading && wallets.length > 0 && (
        <div className="wallet-list">
          <p className="wallet-count">{wallets.length} of {walletsMax} wallets</p>
          {wallets.map(w => (
            <div key={w.id} className={`wallet-list-item ${w.is_primary ? 'primary' : ''}`}>
              <div className="wallet-list-info">
                <code className="wallet-list-address">{truncateAddress(w.wallet_address)}</code>
                <span className="wallet-list-chain">{w.chain}</span>
                {w.is_primary && <span className="wallet-primary-badge">Primary</span>}
                {w.label && <span className="wallet-label">{w.label}</span>}
              </div>
              <div className="wallet-list-actions">
                {!w.is_primary && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleSetPrimary(w.id)}
                    disabled={settingPrimaryId === w.id}
                  >
                    {settingPrimaryId === w.id ? '...' : 'Set Primary'}
                  </button>
                )}
                <button
                  className="card-remove-btn"
                  onClick={() => handleRemove(w.id)}
                  disabled={removingId === w.id || wallets.length <= 1}
                  title={wallets.length <= 1 ? 'Cannot remove last wallet' : 'Remove wallet'}
                >
                  {removingId === w.id ? '...' : 'Remove'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Connect / Link New Wallet */}
      {linkSuccess && (
        <p className="wallet-hint" style={{color: 'var(--accent-green, #4caf50)'}}>✓ Wallet linked!</p>
      )}

      {linking ? (
        <div className="wallet-link-section">
          <p className="wallet-hint">Signing... check your wallet</p>
        </div>
      ) : (
        <div className="wallet-connect-prompt">
          {linkError && <p className="wallet-error">{linkError}</p>}
          {atLimit ? (
            <p className="wallet-hint wallet-at-limit">
              {walletsMax} of {walletsMax} wallets — remove one to add another
            </p>
          ) : (
            <button
              className="btn btn-primary wallet-connect-btn"
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? 'Connecting...' : (wallets.length > 0 ? 'Connect Another Wallet' : 'Connect Wallet')}
            </button>
          )}
        </div>
      )}

      {/* Payment Preference — show when at least one wallet is linked */}
      {wallets.length > 0 && (
        <div className="payment-preference">
          <h5>Default Payment Method</h5>
          {prefError && <p className="wallet-error">{prefError}</p>}
          
          <fieldset disabled={updatingPref}>
            <label className="preference-option">
              <input type="radio" name="payment-pref" value="stripe"
                checked={currentPreference === 'stripe'}
                onChange={() => handlePreferenceChange('stripe')} />
              <span className="preference-label">Stripe (card)</span>
            </label>
            <label className="preference-option">
              <input type="radio" name="payment-pref" value="wallet"
                checked={currentPreference === 'wallet'}
                onChange={() => handlePreferenceChange('wallet')} />
              <span className="preference-label">USDC (Base)</span>
            </label>
            <label className="preference-option">
              <input type="radio" name="payment-pref" value="both"
                checked={currentPreference === 'both'}
                onChange={() => handlePreferenceChange('both')} />
              <span className="preference-label">None — ask me at payment time</span>
            </label>
          </fieldset>
          {updatingPref && <p className="updating-hint">Updating...</p>}
        </div>
      )}
    </div>
  );
}
